import { Button } from '@design-system/button';
import { FieldToShow, InputType } from '@phx-husky/auth-service';
import { useProfile } from '@phx-husky/use-profile';
import { useTranslations } from '@phx-husky/use-translations';
import classNames from 'classnames';
import React, { useCallback, useState } from 'react';

import { Form } from '../form';
import { UserDetailsSectionProps } from '../models';
import { ChangeEmail } from './change-email';
import { UserDetailsField } from './user-details-field';
import * as styles from './user-details-section.module.scss';

export const UserDetailsSection = ({
  fields,
  editButtonDisabled,
  isLdsConfirmed,
  openConfirmLdsModal,
  onFormCloseCallback,
  changeEmailFields,
}: UserDetailsSectionProps) => {
  const { setUserAttributes, isPasswordProviderUser } = useProfile();
  const [formVisible, setFormVisible] = useState(false);
  const [emailFormVisible, setEmailFormVisible] = useState(false);
  const { translate } = useTranslations();
  const { email: emailField, ...userDetailsFields } = fields;

  const editUserDetailsOrAcceptNewLds = useCallback(() => {
    if (isLdsConfirmed) {
      setFormVisible(true);
    } else {
      openConfirmLdsModal();
    }
  }, [isLdsConfirmed, openConfirmLdsModal]);

  const onClose: UserDetailsSectionProps['onFormCloseCallback'] = (params) => {
    setFormVisible(false);
    onFormCloseCallback(params);
  };

  const setEmailFormOrAcceptNewLds = useCallback(
    (value: boolean) => {
      if (isLdsConfirmed) {
        setEmailFormVisible(value);
      } else {
        openConfirmLdsModal();
      }
    },
    [isLdsConfirmed, openConfirmLdsModal]
  );

  const prepareValue = (field: FieldToShow, fieldValue: any) => {
    if (field.type !== InputType.select || !field.options) {
      return fieldValue;
    }

    return field.options.find((option) => option.value === fieldValue);
  };

  return (
    <div className={classNames(styles.section, 'auth-profile-section')}>
      {formVisible ? (
        <Form
          fields={userDetailsFields}
          cancelButtonLabel={translate('userDetailsEditCancelButton')}
          submitButtonLabel={translate('userDetailsEditSubmitButton')}
          onClose={onClose}
          onSubmit={setUserAttributes}
          closeButtonVisible
          updateFieldsOnSubmit
        />
      ) : (
        <>
          {Object.entries(userDetailsFields)
            .filter(([, { showInPreview }]) => showInPreview)
            .map(([propertyKey, field]) => (
              <UserDetailsField
                key={propertyKey}
                label={field.label}
                defaultValue={
                  prepareValue(field, field.defaultValue)?.label ||
                  field.defaultValue
                }
              />
            ))}
          <div
            className={classNames(
              styles.buttonWrapper,
              'auth-profile-section__button-wrapper'
            )}
          >
            <Button
              variant="primary"
              className={classNames(
                styles.button,
                'auth-profile-section__button-edit'
              )}
              type="button"
              onClick={editUserDetailsOrAcceptNewLds}
              disabled={editButtonDisabled}
            >
              {translate('userDetailsEditButton')}
            </Button>
          </div>
        </>
      )}

      {!emailFormVisible && (
        <UserDetailsField
          key="email"
          label={emailField.label}
          defaultValue={emailField.defaultValue}
        />
      )}
      {isPasswordProviderUser() && (
        <ChangeEmail
          fields={changeEmailFields}
          editButtonDisabled={editButtonDisabled}
          formVisible={emailFormVisible}
          setFormVisible={setEmailFormOrAcceptNewLds}
        />
      )}
    </div>
  );
};
